/**
 * Near-duplicate drafts: the generator often leaves several versions of one
 * page or post behind ("Best RFP Response Software… Buyer's Guide" ×4). The
 * library shows one card per title and language, the newest, with the others
 * one click away.
 */

/**
 * Little words that the generator adds or drops between versions of one title
 * ("Best RFP Response Software in 2026" vs "Best RFP Response Software 2026").
 */
const LITTLE_WORDS = new Set(['a', 'an', 'the', 'in', 'of', 'for']);

/** A four-digit year, which the generator puts in some versions and not others. */
const YEAR = /^(19|20)\d{2}$/;

/**
 * Lower-cased, punctuation and spacing folded, little words and years dropped,
 * so "Best RFP Response Software in 2026: Buyer’s Guide" and "Best RFP response
 * software: buyers guide" match.
 */
export function normaliseTitle(title: string | null | undefined): string {
  return (title ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w && !LITTLE_WORDS.has(w) && !YEAR.test(w))
    .join(' ');
}

/**
 * A generation that failed before it wrote a title leaves the first research
 * bullet as the title ("- The strongest SEO openings…") and the reason in
 * `metadata.error`. Returns that reason (first sentence) for such drafts, else null.
 */
export function failedGeneration(item: { title: string | null; metadata?: unknown }): string | null {
  const meta = (item.metadata ?? {}) as Record<string, unknown>;
  const looksLikeBullet = /^\s*([-*\u2022]|#{1,6})\s/.test(item.title ?? '');
  if (!looksLikeBullet) return null;
  const error = typeof meta.error === 'string' ? meta.error : '';
  return error ? error.split(/(?<=\.)\s|\s\|\s/)[0]!.trim() : 'No title was generated.';
}

export interface TitleGroup<T> {
  /** The newest version, shown on the card. */
  item: T;
  /** Older versions with the same title, newest first. */
  older: T[];
}

/**
 * Word overlap (Jaccard over normalised title words) at or above this reads as
 * one title. "Best RFP Response Software for Regulated Industries 2026" and
 * "RFP Response Software for Regulated Industries 2026" share 5 of 6 words
 * (0.83) and merge; "Best RFP Response Software" vs "Best RFQ Response
 * Software" share 3 of 5 (0.6) and stay apart.
 */
export const TITLE_SIMILARITY = 0.8;

/** Titles shorter than this many words only merge on an exact normalised match. */
const MIN_FUZZY_WORDS = 4;

/** Pure: Jaccard similarity of two word sets. */
export function wordSimilarity(a: ReadonlySet<string>, b: ReadonlySet<string>): number {
  if (a.size === 0 && b.size === 0) return 0;
  let shared = 0;
  for (const w of a) if (b.has(w)) shared += 1;
  return shared / (a.size + b.size - shared);
}

/**
 * One group per normalised title + language, newest first by `updatedAt`.
 * Titles that differ by one word ("Best …" vs "…") merge too, by word overlap
 * (TITLE_SIMILARITY). Items with no title are never merged. Drafts whose
 * generation failed have no real title, so they group by their error instead:
 * on 12 Oct, seven "Untitled draft: generation failed" cards on Website › SEO
 * Article became one. Order of the groups follows the order of their newest
 * item in `items` sorted newest first.
 */
export function groupByTitle<
  T extends { id: string; title: string | null; language?: string | null; updatedAt: string; metadata?: unknown },
>(
  items: T[],
): TitleGroup<T>[] {
  const sorted = [...items].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const byKey = new Map<string, TitleGroup<T>>();
  const fuzzy: Array<{ language: string; words: Set<string>; group: TitleGroup<T> }> = [];
  const groups: TitleGroup<T>[] = [];
  for (const item of sorted) {
    const failure = failedGeneration(item);
    const title = normaliseTitle(item.title);
    const language = item.language ?? '';
    const key = failure ? `failed|${failure}` : title ? `${language}|${title}` : `id|${item.id}`;
    let hit = byKey.get(key);
    const words = !failure && title ? new Set(title.split(' ')) : null;
    if (!hit && words && words.size >= MIN_FUZZY_WORDS) {
      hit = fuzzy.find((f) => f.language === language && wordSimilarity(f.words, words) >= TITLE_SIMILARITY)?.group;
      if (hit) byKey.set(key, hit);
    }
    if (hit) {
      hit.older.push(item);
      continue;
    }
    const group = { item, older: [] as T[] };
    byKey.set(key, group);
    if (words && words.size >= MIN_FUZZY_WORDS) fuzzy.push({ language, words, group });
    groups.push(group);
  }
  return groups;
}

/* ─── Generation rules: near-duplicates and contradictions ─── */

export interface RuleLike {
  id: string;
  rule: string;
  priority: number;
  isActive: boolean;
}

export type RuleIssueKind = 'em-dash' | 'product-name' | 'opposite-wording' | 'near-duplicate';

export interface RuleIssue {
  kind: RuleIssueKind;
  /** The rule that breaks or repeats the other one. */
  ruleId: string;
  /** The rule it contradicts or repeats. */
  otherId: string;
  /** Plain sentence for the Rules page. */
  message: string;
}

/** Lowercased words longer than two letters, punctuation dropped. */
export function ruleWords(text: string): Set<string> {
  return new Set(text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter((w) => w.length > 2));
}

/** Word overlap at or above this reads as the same rule. */
export const RULE_NEAR_DUPLICATE = 0.8;

const EM_DASH = '\u2014';
const BANS_EM_DASH = /\b(never|don['’]?t|do not|avoid|no)\b[^.]{0,40}\bem[\s-]?dash/i;

/** "SEQUESTO aOS", "SEQUESTO AOS", "SEQUESTO OS" or a bare "aOS"/"AOS": the spelling is case-sensitive. */
const PRODUCT_NAME = /\bSEQUESTO\s+(aOS|AOS|OS)\b|\b(aOS|AOS)\b/g;

/**
 * `"X" … not / rather than / instead of … "Y"`: the rule prefers X and bans Y.
 * Bounded gaps so a quote from another clause doesn't pair up.
 */
const PREFER_OVER = /"([^"]{1,60})"[^".;]{0,60}?\b(?:not|rather than|instead of)\b[^"]{0,40}"([^"]{1,60})"/gi;

function productNames(text: string): Set<string> {
  const names = new Set<string>();
  for (const m of text.matchAll(PRODUCT_NAME)) names.add((m[1] ?? m[2])!);
  return names;
}

function preferences(text: string): Array<{ prefer: string; ban: string }> {
  const out: Array<{ prefer: string; ban: string }> = [];
  for (const m of text.matchAll(PREFER_OVER)) {
    const prefer = m[1]!.trim().toLowerCase();
    const ban = m[2]!.trim().toLowerCase();
    if (prefer && ban && prefer !== ban) out.push({ prefer, ban });
  }
  return out;
}

const label = (r: RuleLike) => `P${r.priority} "${r.rule.trim().slice(0, 60)}${r.rule.trim().length > 60 ? '…' : ''}"`;

/**
 * Pure: active rules whose text breaks another active rule, and active rules
 * that say nearly the same thing. Read-only advice for the Rules page; it
 * never changes a rule. Checks:
 * - em dash: a rule bans the em dash and another rule's text uses one (the
 *   generator copies the example wording, so the ban loses);
 * - product name: active rules spell the product differently ("aOS", "AOS",
 *   "SEQUESTO OS"); each is flagged against the highest-priority spelling;
 * - opposite wording: one rule says "X" not "Y" and another says "Y" not "X"
 *   (both terms reversed, not merely one shared);
 * - near-duplicate: two active rules share RULE_NEAR_DUPLICATE of their words.
 */
export function findRuleIssues(rules: readonly RuleLike[]): RuleIssue[] {
  const active = rules.filter((r) => r.isActive).sort((a, b) => b.priority - a.priority);
  const issues: RuleIssue[] = [];

  const dashBan = active.find((r) => BANS_EM_DASH.test(r.rule));
  if (dashBan) {
    for (const r of active) {
      if (r.id === dashBan.id || BANS_EM_DASH.test(r.rule) || !r.rule.includes(EM_DASH)) continue;
      issues.push({
        kind: 'em-dash',
        ruleId: r.id,
        otherId: dashBan.id,
        message: `Uses an em dash, which ${label(dashBan)} bans. Drafts copy rule wording, so replace it with a comma or full stop.`,
      });
    }
  }

  const named = active.map((r) => ({ r, names: productNames(r.rule) })).filter((x) => x.names.size > 0);
  const reference = named[0];
  if (reference) {
    const refName = [...reference.names][0]!;
    for (const { r, names } of named.slice(1)) {
      const other = [...names].find((n) => !reference.names.has(n));
      if (!other) continue;
      issues.push({
        kind: 'product-name',
        ruleId: r.id,
        otherId: reference.r.id,
        message: `Writes the product as "${other === 'OS' ? 'SEQUESTO OS' : other}", while ${label(reference.r)} writes "${refName}".`,
      });
    }
  }

  const prefs = active.map((r) => ({ r, prefs: preferences(r.rule) }));
  for (const a of prefs) {
    for (const b of prefs) {
      if (a.r.id >= b.r.id) continue;
      // A real reversal: one rule says "X" not "Y" and the other "Y" not "X". One shared
      // term isn't enough: on 13 Oct P9 ("the SEQUESTO AOS" not "SEQUESTO") was flagged
      // against P8 ("SEQUESTO" rather than "agents"), which agree.
      let term: string | null = null;
      for (const p of a.prefs) {
        const q = b.prefs.find((x) => x.prefer === p.ban && x.ban === p.prefer);
        if (q) {
          term = p.prefer;
          break;
        }
      }
      if (!term) continue;
      const [lower, higher] = a.r.priority <= b.r.priority ? [a.r, b.r] : [b.r, a.r];
      issues.push({
        kind: 'opposite-wording',
        ruleId: lower.id,
        otherId: higher.id,
        message: `Disagrees with ${label(higher)} on "${term}": one asks for it, the other rules it out.`,
      });
    }
  }

  const words = active.map((r) => ({ r, w: ruleWords(r.rule) }));
  for (let i = 0; i < words.length; i += 1) {
    for (let j = i + 1; j < words.length; j += 1) {
      if (wordSimilarity(words[i]!.w, words[j]!.w) < RULE_NEAR_DUPLICATE) continue;
      issues.push({
        kind: 'near-duplicate',
        ruleId: words[j]!.r.id,
        otherId: words[i]!.r.id,
        message: `Says nearly the same as ${label(words[i]!.r)}. Keep one.`,
      });
    }
  }
  return issues;
}

/** Pure: every rule on either side of an issue, once. */
export function issueRuleIds(issues: readonly RuleIssue[]): Set<string> {
  return new Set(issues.flatMap((i) => [i.ruleId, i.otherId]));
}

/** Pure: the issues each rule is part of, on either side, for the per-rule badge. */
export function issuesPerRule(issues: readonly RuleIssue[]): Map<string, RuleIssue[]> {
  const map = new Map<string, RuleIssue[]>();
  for (const i of issues) {
    for (const id of new Set([i.ruleId, i.otherId])) map.set(id, [...(map.get(id) ?? []), i]);
  }
  return map;
}

/* ─── Card excerpts ─── */

/** Words kept upper-case when an all-caps heading is put in sentence case. */
const KEEP_CASE: Record<string, string> = {
  RFP: 'RFP', RFPS: 'RFPs', RFI: 'RFI', RFQ: 'RFQ', RFX: 'RFx', ITT: 'ITT', PQQ: 'PQQ', DDQ: 'DDQ',
  AI: 'AI', UK: 'UK', US: 'US', EU: 'EU', B2B: 'B2B', SAAS: 'SaaS', SEO: 'SEO', GDPR: 'GDPR',
  ISO: 'ISO', CRM: 'CRM', API: 'API', LLM: 'LLM', LLMS: 'LLMs', SEQUESTO: 'SEQUESTO', AOS: 'aOS',
};

/** An all-caps line ("THE SURGE IN AI PROCUREMENT…") in sentence case, acronyms kept. */
function sentenceCase(line: string): string {
  const letters = line.replace(/[^A-Za-z]/g, '');
  if (letters.length < 8 || letters !== letters.toUpperCase()) return line;
  const words = line.split(/(\s+)/).map((w) => {
    const bare = w.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    return KEEP_CASE[bare] ? w.replace(/[A-Za-z0-9]+/, KEEP_CASE[bare]) : w.toLowerCase();
  });
  const joined = words.join('');
  return joined.replace(/[a-z]/, (c) => c.toUpperCase());
}

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)+$/;
/** A heading-sized line: a few words and no closing punctuation. */
const HEADING_WORDS = 8;

/**
 * Pure: a card title, with a slug ("bid-no-bid", "rfp-response-software") read
 * as words ("Bid no bid", "RFP response software"). Glossary drafts made from a
 * Sanity slug kept it as their title.
 */
export function displayTitle(title: string | null | undefined): string {
  const t = (title ?? '').trim();
  if (!SLUG_RE.test(t)) return t;
  const words = t.split('-').map((w) => KEEP_CASE[w.toUpperCase()] ?? w);
  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/**
 * Pure: the first `max` characters of a draft as one readable run of text.
 * Headings and paragraphs are separate lines in the body; joined as they were,
 * a heading ran into the next sentence ("…with a search bar The strongest") and
 * all-caps headings shouted. Each line now ends with a full stop if it had no
 * closing punctuation, all-caps lines go to sentence case, markdown marks are
 * dropped, and the cut falls on a word boundary.
 *
 * Leading headings are dropped, since the card title already sits above the
 * excerpt: a slug line, a line repeating the title or the line before it, a
 * markdown heading, or a short line with no closing punctuation. On 12 Oct the
 * cards read "go-no-go. A go/no-go decision…", "What is RFP Response Software.
 * RFP Software. RFP software refers…" and "A game changer… A game changer…".
 * The body's last line is always kept.
 */
export function draftExcerpt(body: string, max = 200, title?: string | null): string {
  const raw = body.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const titleKey = normaliseTitle(title ?? null);
  const seen = new Set<string>();
  let start = 0;
  while (start < raw.length - 1) {
    const line = raw[start]!;
    const bare = line.replace(/^\s*(#{1,6}|[-*>]|\d+\.)\s+/, '').replace(/[*_`]+/g, '').trim();
    const key = normaliseTitle(bare);
    const isHeading =
      SLUG_RE.test(bare) ||
      (!!titleKey && key === titleKey) ||
      seen.has(key) ||
      /^#{1,6}\s/.test(line) ||
      (bare.split(/\s+/).length <= HEADING_WORDS && !/[.!?:;…"'”’)]$/.test(bare));
    if (!isHeading) break;
    seen.add(key);
    start += 1;
  }
  const lines = raw
    .slice(start)
    .map((l) => l.replace(/^\s*(#{1,6}|[-*>]|\d+\.)\s+/, '').replace(/[*_`]+/g, '').trim())
    .filter(Boolean)
    .map((l) => sentenceCase(l))
    .map((l) => (/[.!?:;…"'”’)]$/.test(l) ? l : `${l}.`));
  const text = lines.join(' ').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[,;:.]$/, '')}…`;
}
